import React, { FC, FormEvent, useCallback, useRef, useState } from 'react';
import { FormFeedback, Input } from 'reactstrap';
import { UniqueAttribute } from '../APIClient';
import { useTranslation } from '../translations';
import { delayPromise, randInt } from '../utils';
import { APIError } from '../utils/APIClient';

export enum UniqueInputState {
  INVALID = -2,
  INUSE = -1,
  UNCHECKED = 0,
  AVAILABLE = 1,
}

export interface UniqueAttributeProps {
  readonly type: UniqueAttribute;
  readonly onChange?: (val: string) => void;
}
export interface UniqueAttributeState {
  state: UniqueInputState;
}

const StateMessage: FC<{
  readonly type: UniqueAttribute;
  readonly state: UniqueInputState;
}> = ({ type, state }) => {
  const { ts } = useTranslation();
  switch (state) {
    case UniqueInputState.INVALID: {
      if (type === 'username') {
        return ts('unique_attribute_input.that_username_is_invalid_usernames');
      }

      return ts('unique_attribute_input.this_type_is_invalid', type);
    }

    case UniqueInputState.INUSE: {
      return ts('unique_attribute_input.this_type_is_already_in', type);
    }

    case UniqueInputState.AVAILABLE: {
      return ts('unique_attribute_input.great_this_type_is_available', type);
    }

    default: {
      return null;
    }
  }
};

interface UniqueAttributeInputProps {
  readonly type: UniqueAttribute;
  readonly onChange?: (val: string) => void;
}

export const UniqueAttributeInput: FC<UniqueAttributeInputProps> = ({ type, onChange }) => {
  const { ts } = useTranslation();
  const [state, setState] = useState<UniqueInputState>(UniqueInputState.UNCHECKED);
  const ref = useRef<HTMLInputElement>(null);
  const checkUnique = useCallback(
    async (event: FormEvent<HTMLInputElement>, field: UniqueAttribute) => {
      let localState = UniqueInputState.INVALID;
      const { value } = event.currentTarget;
      try {
        // If exists, 200. If not found, 404.
        localState = (await api.checkUserAttributeUnique(value, field))
          ? UniqueInputState.AVAILABLE
          : UniqueInputState.INUSE;
      } catch (error) {
        if (!(error instanceof APIError) || error.apiResponse.errors.rateLimit) {
          throw error;
        }

        await delayPromise(randInt(100, 250));
      }

      setState(localState);
    },
    [setState],
  );

  return (
    <>
      <Input
        autoComplete={type === 'username' ? 'username' : 'email'}
        id={type}
        innerRef={ref}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        invalid={state < 0}
        name={type}
        onBlur={async (el) => {
          await checkUnique(el, type);
        }}
        onChange={(el) => onChange?.(el.currentTarget.value)}
        placeholder={`${ts('common.example')}: ${
          type === 'email' ? 'bobcat@concat.app' : 'bobcat18'
        }`}
        type={type === 'email' ? 'email' : 'text'}
      />
      {state !== UniqueInputState.UNCHECKED && (
        <FormFeedback
          invalid={state === UniqueInputState.INVALID ? 'true' : undefined}
          valid={state === UniqueInputState.AVAILABLE}
        >
          <StateMessage state={state} type={type} />
        </FormFeedback>
      )}
    </>
  );
};
